import React, { useContext, useEffect, useState } from 'react';

import { CombatContext } from '../contexts/CombatContext';
import { activeFamilyOf, familyArrayKey } from '../encounter/encounterStoreEngine';
import { runtimeContextPayload } from './combatContextIdentity';
import { ensureSharingContext } from './sharingApi';
import { fetchGeneratorContext, syncGeneratorContext } from './combatProRuntimeApi';
import { openSpecialistSource } from './provenanceApi';
import { launchGeneratorInDcc } from './dccGeneratorLaunch';
import { ActionPill, StatusBadge, WorkspaceCard, useLayeredModal } from './ui/LayeredWorkspace';

export const CONTEXT_GROUP_ORDER = [
  'Encounter', 'Objectives', 'Battlefield', 'Hazards', 'Tactics', 'Clocks',
  'Companions', 'Environment', 'Factions', 'Items/Rewards', 'Other active context'
];

function text(value) { return value === null || typeof value === 'undefined' ? '' : String(value); }

export function buildLiveGeneratorContext(state) {
  const store = state.encounterStore || {};
  const family = activeFamilyOf(store);
  const index = typeof store.activeIndex === 'number' ? store.activeIndex : 0;
  const encounters = Array.isArray(store[familyArrayKey(family)]) ? store[familyArrayKey(family)] : [];
  const encounter = encounters[index] || {};
  const brief = encounter.encounterBrief || {};
  const dmOps = encounter.dmOps || {};
  const cards = [];
  const encounterName = ((encounter.meta || {}).name || ('Encounter ' + (index + 1)));
  cards.push({
    runtime_key: 'encounter:' + family + ':' + index, group: 'Encounter', title: encounterName,
    summary: 'Round ' + (state.currentRound || 1) + ' is current.', active: true,
    affects: 'All combat participants', status: 'ACTIVE', visibility: 'PARTY',
    player_projection: { title: encounterName, summary: 'Round ' + (state.currentRound || 1), affects: 'All combat participants', status: 'ACTIVE' }
  });
  if (brief.goal && brief.goal.objective) {
    cards.push({
      runtime_key: 'objective:primary', group: 'Objectives', title: text(brief.goal.objective),
      summary: text(brief.goal.resolution || 'Open'), active: brief.goal.resolution !== 'resolved',
      status: text(brief.goal.resolution || 'ACTIVE').toUpperCase(), visibility: 'PARTY',
      player_projection: { title: text(brief.goal.objective), summary: text(brief.goal.resolution || 'Open'), status: text(brief.goal.resolution || 'ACTIVE').toUpperCase() }
    });
  }
  (Array.isArray(brief.rules) ? brief.rules : []).forEach(function (rule, ruleIndex) {
    const hazard = rule.kind === 'hazard';
    cards.push({
      runtime_key: (hazard ? 'hazard:' : 'battlefield:') + ruleIndex,
      group: hazard ? 'Hazards' : (rule.kind === 'environment' ? 'Environment' : 'Battlefield'),
      title: text(rule.title || rule.name || (hazard ? 'Hazard' : 'Battlefield rule')),
      summary: text(rule.detail || rule.description), active: rule.status !== 'resolved',
      affects: text(rule.affects), status: text(rule.status || 'ACTIVE').toUpperCase(),
      source_generator_slug: text(rule.sourceGeneratorSlug), visibility: rule.playerSafe === false ? 'DM_ONLY' : 'PARTY',
      player_projection: rule.playerSafe === false ? {} : { title: text(rule.title || rule.name), summary: text(rule.detail || rule.description), affects: text(rule.affects), status: text(rule.status || 'ACTIVE').toUpperCase() }
    });
  });
  (Array.isArray(dmOps.quickActions) ? dmOps.quickActions : []).forEach(function (action, actionIndex) {
    cards.push({ runtime_key: 'tactic:' + (action.id || actionIndex), group: 'Tactics', title: text(action.label || action.kind || 'Tactic'), summary: text(action.detail), active: true, status: 'READY', visibility: 'DM_ONLY', player_projection: {} });
  });
  (Array.isArray(dmOps.triggeredEffects) ? dmOps.triggeredEffects : []).filter(function (clock) { return clock.enabled !== false; }).forEach(function (clock, clockIndex) {
    const dueRound = typeof clock.round === 'number' ? clock.round : null;
    cards.push({
      runtime_key: 'clock:' + (clock.id || clockIndex), group: 'Clocks', title: text(clock.name || 'Combat clock'),
      summary: text(clock.detail), active: true,
      time_remaining: dueRound ? (Math.max(0, dueRound - (state.currentRound || 1)) + ' rounds remaining') : text(clock.when),
      status: dueRound && dueRound <= (state.currentRound || 1) ? 'DUE' : 'ACTIVE', visibility: 'DM_ONLY', player_projection: {}
    });
  });
  (state.players || []).filter(function (player) { return player && player.companion; }).forEach(function (companion, companionIndex) {
    cards.push({
      runtime_key: 'companion:' + text(companion.id || companionIndex), group: 'Companions',
      title: text(companion.name || 'Companion'), summary: 'Combat eligible', active: companion.active !== false,
      target_owner: text(companion.ownerName || companion.owner || ''), status: 'ACTIVE', visibility: 'PARTY',
      player_projection: { title: text(companion.name || 'Companion'), summary: 'Combat eligible', target_owner: text(companion.ownerName || companion.owner || ''), status: 'ACTIVE' }
    });
  });
  return cards.filter(function (card) { return card.active !== false; });
}

function ContextDetail({ card }) {
  const [notice, setNotice] = useState('');
  async function openSource() {
    try {
      const result = await openSpecialistSource(card.source_generator_slug);
      await launchGeneratorInDcc(result);
      setNotice('Source generator opened in DCC.');
    } catch (error) { setNotice(error && error.message === 'dcc_required' ? 'Open this source in the VISINVOKE Desktop Control Centre.' : 'The source generator is not owned, available or authorised for this DCC session.'); }
  }
  return <div className="cp-context-detail">
    <StatusBadge tone={String(card.status || 'active').toLowerCase()}>{card.status || 'ACTIVE'}</StatusBadge>
    <h3>{card.title}</h3><p>{card.detail || card.summary}</p>
    {card.affects ? <p><strong>Affects:</strong> {card.affects}</p> : null}
    {card.time_remaining ? <p><strong>Time remaining:</strong> {card.time_remaining}</p> : null}
    {card.target_owner ? <p><strong>Target / owner:</strong> {card.target_owner}</p> : null}
    {card.source_generator_slug ? <ActionPill primary onClick={openSource}>Open source generator</ActionPill> : null}
    {notice ? <p role="status">{notice}</p> : null}
  </div>;
}

function Board({ cards, playerSafe }) {
  const { openLayer } = useLayeredModal();
  return <div className="cp-context-board" data-player-safe={playerSafe ? 'true' : 'false'}>
    {CONTEXT_GROUP_ORDER.filter(function (group) { return cards.some(function (card) { return card.group === group; }); }).map(function (group) {
      return <section key={group} className="cp-context-group"><h3>{group}</h3><div className="cp-information-grid">
        {cards.filter(function (card) { return card.group === group; }).map(function (card) {
          const meta = [card.affects, card.time_remaining, card.target_owner].filter(Boolean).join(' · ');
          return <WorkspaceCard key={card.id || card.runtime_key} type={group.toUpperCase()} title={card.title} summary={card.summary} meta={meta} status={card.status || 'ACTIVE'} source={card.source_generator_slug} onOpen={function () { openLayer({ title: card.title, eyebrow: group, subtitle: 'Current linked combat context', content: <ContextDetail card={card} /> }); }} />;
        })}
      </div></section>;
    })}
    {!cards.length ? <p className="cp-empty-copy">No linked current context for this encounter.</p> : null}
  </div>;
}

export function DmGeneratorContextWorkspace() {
  const combat = useContext(CombatContext);
  const [cards, setCards] = useState([]);
  const [notice, setNotice] = useState('Reading current Combat PRO state…');
  useEffect(function () {
    let live = true;
    ensureSharingContext(runtimeContextPayload(combat.state)).then(function (identity) {
      return syncGeneratorContext({ campaign_id: identity.campaign_id, encounter_id: identity.encounter_id, cards: buildLiveGeneratorContext(combat.state) });
    }).then(function (result) {
      if (live) { setCards(result.cards || []); setNotice('Live board updated from current Combat PRO state.'); }
    }).catch(function () { if (live) setNotice('Generator Context could not connect.'); });
    return function () { live = false; };
  }, [combat.state]);
  return <div><p role="status">{notice}</p><Board cards={cards} playerSafe={false} /></div>;
}

export function PlayerGeneratorContextWorkspace({ campaignId }) {
  const [cards, setCards] = useState([]);
  const [notice, setNotice] = useState('Loading player-safe combat context…');
  useEffect(function () {
    let live = true;
    fetchGeneratorContext(campaignId).then(function (result) { if (live) { setCards(result.cards || []); setNotice('Player-safe context only.'); } }).catch(function () { if (live) setNotice('No current player context is available.'); });
    return function () { live = false; };
  }, [campaignId]);
  return <div><p role="status">{notice}</p><Board cards={cards} playerSafe={true} /></div>;
}

export default function GeneratorContextChip({ mode, campaignId }) {
  const { openLayer } = useLayeredModal();
  const player = mode === 'player';
  return <button type="button" className="cp-workspace-chip" onClick={function () { openLayer({
    title: 'Generator Context', eyebrow: player ? 'Player Context' : 'DM Live Board',
    subtitle: player ? 'Only information approved for players.' : 'Current linked generator-derived combat context; empty groups are hidden.',
    content: player ? <PlayerGeneratorContextWorkspace campaignId={campaignId} /> : <DmGeneratorContextWorkspace />
  }); }}>Generator Context</button>;
}
